<template>
  <div class="author mk-container">
    <Breadcrumbs :breadcrumbs="breadcrumbs" />
    <div v-if="isLoaded && author.id" class="container--author">
      <div class="introduction">
        <div class="author-avatar">
          <div :style="coverBackgroundImage(author)" class="avatar">
            <!--  -->
          </div>
        </div>
        <div class="author-info">
          <h1 class="name">
            {{ author.name }}
          </h1>
          <div class="description">
            {{ author.description }}
          </div>
        </div>
        <div class="author-achievement">
          <div class="square">
            <div class="item">
              {{ $t('COMMON.ARTICLE') }}
            </div>
            <div class="statistics">
              {{ renderArticleTotal }}
            </div>
          </div>
          <div class="square popularity">
            <div class="item">
              {{ $t('AUTHOR.POPULARITY') }}
            </div>
            <div class="statistics">
              {{ renderPopularity }}
            </div>
          </div>
        </div>
      </div>
      <div class="collection">
        <ReadArticleList :article-list="articleList" />
        <div v-if="!loading && loadMoreCounter > 2">
          <LoadMoreBtn :btn-content="$t('ARTICLE.MORE')" :on-click="manualLoadMore" />
        </div>
      </div>
      <div class="more">
        <Loader :loading="loading" />
      </div>
    </div>
    <div v-else-if="isLoaded && !author.id" class="container--loader">
      <CommonEmptyContent :locale-key="'COMMON.EMPTY_CONTENT_TEXT'" />
    </div>
    <div v-else class="container--loader">
      <Loader :loading="!isLoaded" />
    </div>
  </div>
</template>

<script>
import AuthorService from '@/assets/services/AuthorService'
import ArticleService from '@/assets/services/ArticleService'

import CommonEmptyContent from '@/components/common/DummyStatus/CommonEmptyContent'
import Loader from '@/components/common/Loader'
import Breadcrumbs from '@/components/common/Breadcrumbs'
import LoadMoreBtn from '@/components/common/LoadMoreBtn'
import ReadArticleList from '@/components/read/ReadArticleList'

export default {
  name: 'AuthorById',
  components: {
    Breadcrumbs,
    Loader,
    CommonEmptyContent,
    ReadArticleList,
    LoadMoreBtn
  },
  directives: {},
  props: {},
  data() {
    return {
      // breadcrumbs: this.$route.meta.breadcrumbs,
      page: {
        perPage: 10, // default
        current: 1,
        last: 0,
        total: 0
      },
      author: undefined,
      articleList: undefined,
      loading: false,
      loadMoreDebounceTimer: 0,
      loadMoreCounter: 0
    }
  },
  metaInfo() {
    return {
      title: this.title,
      meta: [
        ...this.$route.meta.metaInfo.meta,
        { vmid: 'name', name: 'name', content: this.title },
        { vmid: 'description', name: 'description', content: this.description },
        { vmid: 'image', name: 'image', content: this.cover },
        { vmid: 'keywords', name: 'keywords', content: this.keywords },
        { vmid: 'og:title', name: 'og:title', content: this.title },
        { vmid: 'og:description', name: 'og:description', content: this.description },
        { vmid: 'og:image', name: 'og:image', content: this.cover },
        { vmid: 'og:url', name: 'og:url', content: this.canonicalURL }
      ],
      link: [
        { vmid: 'canonical', rel: 'canonical', href: this.canonicalURL },
        { vmid: 'image_src', rel: 'image_src', href: this.cover }
      ],
      __dangerouslyDisableSanitizers: ['script'],
      script: [{ innerHTML: JSON.stringify(this.structuredData), type: 'application/ld+json' }]
    }
  },
  computed: {
    breadcrumbs() {
      const breadcrumbsAry = [{ routeName: 'index', localeLabel: 'READ.TITLE', redirectPath: '' }]
      breadcrumbsAry.push({ routeName: 'authorList', localeLabel: 'AUTHOR.LIST', redirectPath: '' })
      if (this.author && this.author.name) {
        breadcrumbsAry.push({ routeName: '', localeLabel: this.author.name, redirectPath: '' })
      } else {
        breadcrumbsAry.push({ routeName: '', localeLabel: 'AUTHOR.TITLE', redirectPath: '' })
      }
      return breadcrumbsAry
    },
    isLoaded() {
      return this.author && this.articleList
    },
    isLastPage() {
      return this.page.last <= this.page.current
    },
    renderArticleTotal() {
      return this.author['articles_count'] && this.author['articles_count'] >= 10000 // transfer over 10K
        ? Number.parseInt(this.author['articles_count'] / 1000) + 'K'
        : this.author['articles_count'] || 0
    },
    renderPopularity() {
      // return this.author.hits && this.author.hits >= 1000
      //   ? Number.parseInt(this.author.hits / 1000) + 'K'
      //   : '< 1k'
      return this.author.hits && this.author.hits >= 1000000000
        ? Number.parseInt(this.author.hits / 1000000000) + 'B'
        : this.author.hits >= 1000000
          ? Number.parseInt(this.author.hits / 1000000) + 'M'
          : this.author.hits >= 1000
            ? Number.parseInt(this.author.hits / 1000) + 'K'
            : '< 1k'
    },
    title() {
      const titlePostfix = `${this.$t('READ.TITLE')} | MUZIK AIR`
      return this.author && this.author.name ? `${this.author.name} | ${titlePostfix}` : titlePostfix
    },
    description() {
      return this.author ? (this.author.description || this.author.name || this.$t('AUTHOR.TITLE')) : this.$t('AUTHOR.TITLE')
    },
    keywords() {
      return this.author ? (this.author.name || this.$t('AUTHOR.TITLE')) : this.$t('AUTHOR.TITLE')
    },
    cover() {
      return this.author && this.author.cover
        ? this.author.cover
        : `https://${process.env.VUE_APP_MUZIK_READ}.${process.env.VUE_APP_MUZIK_DOMAIN}/img/cover-social/read.png`
    },
    canonicalURL() {
      return this.author && this.author.id
        ? `https://${process.env.VUE_APP_MUZIK_READ}.${process.env.VUE_APP_MUZIK_DOMAIN}/${this.$i18n.localePath()}/authors/${this.author.id}`
        : `https://${process.env.VUE_APP_MUZIK_READ}.${process.env.VUE_APP_MUZIK_DOMAIN}`
    },
    structuredData() {
      const data = [
        {
          '@context': 'http://schema.org',
          '@type': 'Person',
          '@id': this.canonicalURL,
          image: this.cover,
          name: (this.author && this.author.name) || this.$t('AUTHOR.TITLE'),
          description: this.description,
          url: []
        },
        {
          '@context': 'http://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              item: {
                '@type': 'Thing',
                '@id': `https://${process.env.VUE_APP_MUZIK_READ}.${process.env.VUE_APP_MUZIK_DOMAIN}/${this.$i18n.localePath()}`,
                name: this.$t('READ.TITLE')
              }
            },
            {
              '@type': 'ListItem',
              position: 2,
              item: {
                '@type': 'Thing',
                '@id': `https://${process.env.VUE_APP_MUZIK_READ}.${process.env.VUE_APP_MUZIK_DOMAIN}/${this.$i18n.localePath()}/authors`,
                name: this.$t('AUTHOR.LIST')
              }
            }
            // {
            //   '@type': 'ListItem',
            //   position: 3,
            //   item: {
            //     '@type': 'Thing',
            //     '@id': this.canonicalURL,
            //     name: (this.author && this.author.name) || this.$t('AUTHOR.TITLE')
            //   }
            // }
          ]
        }
      ]
      if (this.articleList && this.articleList.length > 0) {
        this.articleList.forEach(article => {
          data[0].url.push(`https://${process.env.VUE_APP_MUZIK_READ}.${process.env.VUE_APP_MUZIK_DOMAIN}/${this.$i18n.localePath()}/articles/${article.id}`)
        })
      }
      return data
    }
  },
  watch: {},
  created() {
    const authorId = this.$route.params.id
    const MuzikReadV4Reg = /^[A-F\d]{8}-[A-F\d]{4}-4[A-F\d]{3}-[89AB][A-F\d]{3}-[A-F\d]{12}$/gi
    this.getAuthorById(authorId).then(() => {
      // rewrite old ver id
      if (this.author) {
        this.getArticlesByAuthorId(authorId, this.page.current) // default current page is 1
        if (!MuzikReadV4Reg.test(authorId)) {
          // console.log('Author ID is not uuid v4 format')
          this.$router.replace({ params: { id: this.author.id } })
          this.getArticlesByAuthorId(this.author.id, this.page.current)
        }
      } else {
        this.author = {}
        this.articleList = []
        this.$store.commit('ALERT_MESSAGE', { message: 'COMMON.NOT_FOUND', type: 'error' })
      }
    })
  },
  mounted() {
    window.addEventListener('scroll', this.onScroll)
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.onScroll)
  },
  updated() {},
  methods: {
    coverBackgroundImage(author) {
      return author && author.avatar ? { backgroundImage: `url(${author.avatar})` } : {} // default img in scss
    },
    async getAuthorById(authorId) {
      // const randomUserData = await this.$http.get('https://randomuser.me/api/?nat=us')
      const author = await AuthorService.getAuthorById(authorId)
      if (author) {
        this.author = author
        this.breadcrumbs[this.breadcrumbs.length - 1].localeLabel = this.author.name
        return Promise.resolve(author)
      } else {
        return Promise.resolve(null)
      }
    },
    async getArticlesByAuthorId(authorId, page) {
      this.loading = true
      const params = {
        per_page: this.page.perPage,
        page: page || this.page.current
      }
      const articleListData = await ArticleService.getArticlesByAuthorId(authorId, params)
      if (articleListData) {
        this.articleList = this.articleList ? this.articleList.concat(articleListData.data) : articleListData.data
        this.page.total = articleListData['total']
        this.page.last = articleListData['last_page']
      } else {
        this.articleList = []
      }
      this.loading = false
    },
    onScroll() {
      const scrollTop = window.pageYOffset // document.body.scrollTop;
      const windowHeight = window.innerHeight
      const documentHeight = document.documentElement.scrollHeight
      const scrollPercentage = scrollTop / (documentHeight - windowHeight)
      if (scrollPercentage > 0.9 && this.loadMoreCounter < 3) {
        this.debouncedLoadMore()
      }
    },
    debouncedLoadMore() {
      if (!this.isLastPage && !this.loading) {
        this.loading = true
        if (this.loadMoreDebounceTimer) {
          clearTimeout(this.loadMoreDebounceTimer)
          // this.loadMoreDebounceTimer = 0
        }
        this.loadMoreDebounceTimer = setTimeout(() => {
          this.loadMore()
          this.loadMoreDebounceTimer = 0
        }, 300)
      }
    },
    async loadMore() {
      if (this.page.current < this.page.last) {
        await this.getArticlesByAuthorId(this.author.id, this.page.current + 1)
        this.loadMoreCounter = this.loadMoreCounter + 1
        this.page.current++
        this.loading = false
      }
    },
    manualLoadMore() {
      this.loadMoreCounter = 0
      this.debouncedLoadMore()
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/variables.scss';

.author {
  .container--loader {
    min-height: 200px;
    margin: 24px auto;
    text-align: center;
  }
  .container--author {
    min-height: 200px;
    margin: 24px 0;
    font-size: 16px;
    color: #7b7b7b;
  }
  .introduction {
    min-height: 200px;
    margin-bottom: 72px;
  }
  .more {
    text-align: center;
  }
  &-avatar {
    display: inline-block;
    vertical-align: top;
    width: 130px;
    height: 130px;
    .avatar {
      height: 130px;
      width: 130px;
      border-radius: 50%;
      background-color: $grey-ec;

      background-image: url('/img/img-default/img-user-default-l.png');
      background-repeat: no-repeat;
      background-position: center;
      background-size: cover;
    }
  }
  &-info {
    display: inline-block;
    vertical-align: top;
    width: calc(((100% - 130px) - 400px) - 59px); // (24 * 2) + inline-block offset
    margin-left: 24px;
    margin-right: 24px;

    .name {
      height: 40px;
      margin: 0;
      font-size: 24px;
      font-weight: 500;
      font-style: normal;
      font-stretch: normal;
      line-height: 1.63;
      letter-spacing: normal;
      color: $grey-50;
      text-align: left;

      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .description {
      height: auto;
      font-size: 16px;
      font-weight: 500;
      font-style: normal;
      font-stretch: normal;
      line-height: 1.6;
      letter-spacing: normal;
      color: $grey-7d;
      text-align: justify;

      // max-height: 128px; (16*1.6) * 5
      // word-wrap: break-word;
      // overflow: hidden;
      // text-overflow: ellipsis;
      // display: -webkit-box;
      // -webkit-line-clamp: 5;
      // -webkit-box-orient: vertical;
    }
  }
  &-achievement {
    display: inline-block;
    vertical-align: top;
    width: 400px;
    text-align: right;
    .square {
      display: inline-block;
      vertical-align: top;
      width: 174px;
      height: 174px;
      border: solid 1px $grey-ec;
      text-align: center;
      .item {
        font-size: 20px;
        font-weight: 500;
        margin-top: 24px;
        margin-bottom: 16px;
        line-height: 1.6;
        letter-spacing: normal;
        color: $grey-50;
      }
      .statistics {
        font-size: 48px;
        font-weight: 500;

        line-height: 1.63;
        letter-spacing: normal;
        color: $grey-50;
      }
    }
    .square:nth-child(2n) {
      margin-left: 36px;
    }
  }
}
@media only screen and (min-width: 1440px) {
  // As same as default
}

@media only screen and (min-width: 1024px) and (max-width: 1439.98px) {
  // As same as default
}

@media only screen and (min-width: 768px) and (max-width: 1023.98px) {
  .author {
    .container--author {
      margin: 20px 0px;
    }
    .introduction {
      height: auto;
    }
    &-avatar {
      display: block;
      margin: 0 auto;
      // margin-right: 0;
    }
    &-info {
      display: block;
      width: 75%;
      min-height: 130px;
      margin: 20px auto;
      .name {
        text-align: center;
      }
    }
    &-achievement {
      display: block;
      width: 100%;
      text-align: center;
      // .square {}
    }
  }
}

@media only screen and (max-width: 767.98px) {
  .author {
    .container--author {
      margin: 20px 0px;
    }
    .introduction {
      height: auto;
    }
    &-avatar {
      display: block;
      margin: 0 auto;
      // margin-right: 0;
    }
    &-info {
      display: block;
      width: 100%;
      min-height: 130px;
      margin: 20px 0;
      .name {
        text-align: center;
      }
    }
    &-achievement {
      display: block;
      width: 100%;
      text-align: center;
    }
  }
}

@media only screen and (max-width: 443.98px) {
  // special case
  .author {
    &-achievement {
      .square {
        display: block;
        margin: 20px auto 0;
      }
      .square:nth-child(2n) {
        margin-left: auto;
      }
    }
  }
}
</style>
